import { useState, useEffect, useCallback } from 'react'
import { Box, Card, CardContent, CardHeader, Button, Alert, CircularProgress, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { analysisService, type LiveAnalysisSession } from '@/services/analysisService'
import { useAuth } from '@/contexts/AuthContext'

export default function LiveAnalysisSessionsPage() {
  const { t } = useTranslation('analysis')
  const { t: tCommon } = useTranslation('common')
  const { token } = useAuth()
  const navigate = useNavigate()
  const [sessions, setSessions] = useState<LiveAnalysisSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSessions = useCallback(async () => {
    if (!token) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await analysisService.getLiveAnalysisSessions()
      setSessions(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToLoadSessions'))
    } finally {
      setLoading(false)
    }
  }, [token, t])

  useEffect(() => {
    loadSessions()
  }, [loadSessions])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'completed':
        return 'primary'
      case 'failed':
        return 'error'
      default:
        return 'default'
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!token) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{t('mustBeLoggedIn')}</Alert>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={loadSessions}>{t('retry')}</Button>
      </Box>
    )
  }

  return (
    <Box>
      <Card>
        <CardHeader
          title={t('liveAnalysisSessions')}
          action={
            <Button variant="contained" onClick={() => navigate('/camera')}>
              {t('startNewSession')}
            </Button>
          }
        />
        <CardContent>
          {sessions.length === 0 ? (
            <Alert severity="info">{t('noSessionsFound')}</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('id')}</TableCell>
                    <TableCell>{tCommon('status')}</TableCell>
                    <TableCell>{t('startedAt')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('endedAt')}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('totalFrames')}</TableCell>
                    <TableCell>{tCommon('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id}>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{session.id}</TableCell>
                      <TableCell>
                        <Chip
                          label={session.status}
                          color={getStatusColor(session.status) as 'success' | 'primary' | 'error' | 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(session.started_at)}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{session.ended_at ? formatDate(session.ended_at) : '-'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{session.total_frames}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/live-analysis/${session.id}`)}
                        >
                          {t('viewResults')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
